<!-- NavBar.vue -->
<template>
  <header
    :class="[
      'fixed top-0 left-0 right-0 z-50 transition-all duration-500',
      isScrolled ? 'bg-white shadow-lg' : 'bg-transparent'
    ]"
  >
    <div class="max-w-7xl mx-auto px-6">
      <!-- Initial Layout (Transparent Background) -->
      <div
        v-if="!isScrolled"
        class="py-4 lg:py-6 transition-all duration-500"
      >
        <!-- Mobile: Logo and Menu Toggle in row / Desktop: Centered Logo -->
        <div class="flex justify-between items-center lg:justify-center lg:mb-6">
          <!-- Logo - Clickable to go home -->
          <router-link to="/" class="cursor-pointer">
            <img src="../assets/icons/hutu-abuja.png" alt="Hutu Logo" class="h-12 lg:h-16">
          </router-link>

          <!-- Mobile Menu Button -->
          <button
            @click="toggleMenu"
            class="lg:hidden text-white p-2"
          >
            <svg v-if="!isMenuOpen" class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
            <svg v-else class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <!-- Desktop Navigation (Hidden on Mobile) -->
        <div class="hidden lg:flex justify-center">
          <nav class="flex items-center gap-8">
            <a
              v-for="item in navItems"
              :key="item.id"
              @click.prevent="scrollToSection(item.id)"
              :class="[
                'relative cursor-pointer transition-colors font-medium group',
                isActive(item.id) ? 'text-brand' : 'text-white hover:text-brand'
              ]"
            >
              <span>{{ item.label }}</span>
              <span :class="[
                'absolute -bottom-1 left-0 h-0.5 bg-gradient-to-r from-brand-light to-brand-dark transition-all duration-300',
                isActive(item.id) ? 'w-full' : 'w-0 group-hover:w-full'
              ]"></span>
            </a>
          </nav>
        </div>
      </div>

      <!-- Scrolled Layout (Horizontal, Single Row) -->
      <div
        v-else
        class="py-4 transition-all duration-500"
      >
        <div class="flex justify-between items-center">
          <!-- Logo - Clickable to go home -->
          <router-link to="/" class="cursor-pointer">
            <img src="../assets/icons/hutu-logo.png" alt="Hutu Logo" class="h-10">
          </router-link>

          <!-- Desktop Navigation -->
          <nav class="hidden lg:flex items-center gap-8">
            <a
              v-for="item in navItems"
              :key="item.id"
              @click.prevent="scrollToSection(item.id)"
              :class="[
                'relative cursor-pointer transition-colors font-medium group',
                isActive(item.id) ? 'text-brand' : 'text-gray-900 hover:text-brand'
              ]"
            >
              <span>{{ item.label }}</span>
              <span :class="[
                'absolute -bottom-1 left-0 h-0.5 bg-gradient-to-r from-brand-light to-brand-dark transition-all duration-300',
                isActive(item.id) ? 'w-full' : 'w-0 group-hover:w-full'
              ]"></span>
            </a>
          </nav>

          <!-- CTA Buttons -->
          <div class="hidden lg:flex items-center gap-4">
            <button class="px-6 py-2.5 bg-gradient-to-r from-brand to-brand-dark text-black rounded-full font-semibold hover:shadow-lg hover:shadow-brand/50 transition-all duration-300">
              Let's Talk
            </button>
          </div>

          <!-- Mobile Menu Button -->
          <button 
            @click="toggleMenu"
            class="lg:hidden text-gray-900 p-2"
          >
            <svg v-if="!isMenuOpen" class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
            <svg v-else class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      <!-- Mobile Menu -->
      <transition name="slide">
        <div
          v-if="isMenuOpen"
          :class="[
            'lg:hidden border-t py-6 px-6',
            isScrolled ? 'bg-white border-brand/20' : 'bg-black/95 backdrop-blur-lg border-brand/20'
          ]"
        >
          <nav class="flex flex-col gap-4">
            <a
              v-for="item in navItems"
              :key="item.id"
              @click.prevent="scrollToSection(item.id)"
              :class="[
                'transition-colors py-2 relative pl-4 cursor-pointer',
                isScrolled
                  ? (isActive(item.id) ? 'text-brand' : 'text-gray-900 hover:text-brand')
                  : (isActive(item.id) ? 'text-brand' : 'text-white hover:text-brand')
              ]"
            >
              <span :class="[
                'absolute left-0 top-1/2 -translate-y-1/2 w-1 bg-gradient-to-b from-brand-light to-brand-dark transition-all duration-300',
                isActive(item.id) ? 'h-full' : 'h-0'
              ]"></span>
              {{ item.label }}
            </a>
            <div class="flex flex-col gap-3 pt-4 border-t border-brand/20">
              <button class="px-6 py-2.5 text-gray-900 border-2 border-brand rounded-full hover:bg-brand hover:text-white transition-all">
                Login
              </button>
              <button class="px-6 py-2.5 bg-gradient-to-r from-brand to-brand-dark text-black rounded-full font-semibold">
                Let's Talk
              </button>
            </div>
          </nav>
        </div>
      </transition>
    </div>
  </header>
</template>

<script>
export default {
  name: 'NavBar',
  data() {
    return {
      isMenuOpen: false,
      isScrolled: false,
      activeSection: 'home',
      navItems: [
        { id: 'home', label: 'Home' },
        { id: 'about', label: 'About' },
        { id: 'services', label: 'Services' },
        { id: 'projects', label: 'Projects' },
        { id: 'contact', label: 'Contact' }
      ],
      observer: null
    };
  },
  mounted() {
    window.addEventListener('scroll', this.handleScroll);
    this.$nextTick(() => {
      this.setupScrollSpy();
    });
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.handleScroll);
    if (this.observer) {
      this.observer.disconnect();
    }
  },
  methods: {
    toggleMenu() {
      this.isMenuOpen = !this.isMenuOpen;
    },
    handleScroll() {
      this.isScrolled = window.scrollY > 100;
      // Close mobile menu when scrolling
      if (this.isScrolled && this.isMenuOpen) {
        this.isMenuOpen = false;
      }
    },
    scrollToSection(sectionId) {
      const element = document.getElementById(sectionId);
      if (element) {
        // Section exists on current page, scroll to it
        const navHeight = this.isScrolled ? 80 : 150;
        const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
        window.scrollTo({
          top: elementPosition - navHeight,
          behavior: 'smooth'
        });
        this.isMenuOpen = false;
      } else {
        // Section doesn't exist, navigate to home page with hash
        this.$router.push({ path: '/', hash: `#${sectionId}` });
        this.isMenuOpen = false;
      }
    },
    setupScrollSpy() {
      const options = {
        root: null,
        rootMargin: '-20% 0px -60% 0px',
        threshold: 0
      };

      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.activeSection = entry.target.id;
          }
        });
      }, options);

      // Observe all sections
      this.navItems.forEach((item) => {
        const section = document.getElementById(item.id);
        if (section) {
          this.observer.observe(section);
        }
      });
    },
    isActive(sectionId) {
      return this.activeSection === sectionId;
    }
  }
};
</script>

<style scoped>
.slide-enter-active,
.slide-leave-active {
  transition: all 0.3s ease;
}

.slide-enter-from {
  opacity: 0;
  transform: translateY(-10px);
}

.slide-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
</style>
